import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight, Home } from 'lucide-react'
import { cn } from '@/lib/utils'

interface BreadcrumbItem {
  label: string
  href?: string
  isActive?: boolean
  icon?: React.ComponentType<{ className?: string }>
}

interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items?: BreadcrumbItem[]
  showHome?: boolean
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ items = [], className, showHome = true, ...props }, ref) => {
    const location = useLocation()

    // Auto-generate breadcrumbs if none provided
    const generatedItems = React.useMemo(() => {
      if (items.length > 0) return items

      const pathSegments = location.pathname.split('/').filter(Boolean)
      const generated: BreadcrumbItem[] = []

      if (showHome) {
        generated.push({
          label: 'Dashboard',
          href: '/',
          icon: Home
        })
      }

      pathSegments.forEach((segment, index) => {
        const href = '/' + pathSegments.slice(0, index + 1).join('/')
        const isLast = index === pathSegments.length - 1

        // Convert segment to readable label
        let label = segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')

        // Handle special cases
        if (segment === 'media') label = 'Media & Gallery'
        if (segment === 'galleries') label = 'Gallery Management'
        if (segment === 'approval') label = 'Approval Queue'
        if (segment === 'customers') label = 'Customer Operations'
        if (segment === 'marketing') label = 'Marketing & Social'
        if (segment === 'integrations') label = 'Integrations'
        if (segment === 'gmb') label = 'Google My Business'

        generated.push({
          label,
          href: isLast ? undefined : href,
          isActive: isLast
        })
      })

      return generated
    }, [items, location.pathname, showHome])

    return (
      <nav
        ref={ref}
        className={cn('flex items-center space-x-1 text-sm text-muted-foreground', className)}
        {...props}
      >
        {generatedItems.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <ChevronRight className="h-4 w-4" />}
            <div className="flex items-center">
              {item.icon && <item.icon className="h-4 w-4 mr-1" />}
              {item.href ? (
                <Link
                  to={item.href}
                  className="hover:text-foreground transition-colors"
                >
                  {item.label}
                </Link>
              ) : (
                <span className={cn(
                  'font-medium',
                  item.isActive ? 'text-foreground' : ''
                )}>
                  {item.label}
                </span>
              )}
            </div>
          </React.Fragment>
        ))}
      </nav>
    )
  }
)
Breadcrumb.displayName = "Breadcrumb"

export { Breadcrumb }
export default Breadcrumb